/********************************/
/*          About vuex          */
/********************************/
// The MIT License (MIT)
//
// Copyright (c) 2015-present Evan You
//
// Permission is hereby granted, free of charge, to any person obtaining a copy
// of this software and associated documentation files (the 'Software'), to deal
// in the Software without restriction, including without limitation the rights
// to use, copy, modify, merge, publish, distribute, sublicense, and/or sell
// copies of the Software, and to permit persons to whom the Software is
// furnished to do so, subject to the following conditions:
//
// The above copyright notice and this permission notice shall be included in
// all copies or substantial portions of the Software.
//
// THE SOFTWARE IS PROVIDED 'AS IS', WITHOUT WARRANTY OF ANY KIND, EXPRESS OR
// IMPLIED, INCLUDING BUT NOT LIMITED TO THE WARRANTIES OF MERCHANTABILITY,
// FITNESS FOR A PARTICULAR PURPOSE AND NONINFRINGEMENT. IN NO EVENT SHALL THE
// AUTHORS OR COPYRIGHT HOLDERS BE LIABLE FOR ANY CLAIM, DAMAGES OR OTHER
// LIABILITY, WHETHER IN AN ACTION OF CONTRACT, TORT OR OTHERWISE, ARISING FROM,
// OUT OF OR IN CONNECTION WITH THE SOFTWARE OR THE USE OR OTHER DEALINGS IN
// THE SOFTWARE.

import { mapState, mapActions } from 'vuex';
import session from '../../../../share/session';
import individualDiagnose from '../../../../share/util/individualDiagnose';
import individualWorkSupportUtil from '../../../../share/util/individualWorkSupportUtil';
import { REQUEST_STATUS_COMPLETED, REQUEST_STATUS_FAILED } from '../../../../share/util/api/polling';
import { apiErrorDefaultAction } from '../../../../share/util/api/errorAction';

/** 各フェーズ定義 */
const PHASE = {
  START: 'start',
  SUCCESS: 'success',
  RE_EXECUTE: 'reExecute',
  TIME_OUT: 'timeOut',
  FAILED: 'failed',
};

export default {
  name: 'requirement054',
  data() {
    return {
      modal_requirement_054: false,
      phase: PHASE.START,
      isLoading: false,
      systemId: '',
      bodyItems: [],
      isFirst: true, // 1回目:true, 2回目以降:false
      displayInfo: '',
    };
  },

  props: {
    i18nWsKey: { type: String, default: '', required: true },
    workSupportId: { type: Number, default: '', required: true },
  },
  components: {
  },

  computed: {
    ...mapState({
      netAppId: (state) => state.netAppConnector.pairingAppId,
    }),

    /** ダイアログのヘッダー色 */
    headerColor() {
      switch (this.phase) {
        case PHASE.TIME_OUT:
        case PHASE.FAILED:
          return 'danger';
        case PHASE.SUCCESS:
          return 'success';
        case PHASE.RE_EXECUTE:
        case PHASE.START:
        default:
          return 'secondary';
      }
    },

    headerTitle() {
      // 実行中表示
      const executionTitle = 'individual.common_title_execution';
      // 正常終了表示
      const successTitle = 'message.common_title_success';
      // 再実行確認表示
      const confirmationTitle = 'message.common_title_confirmation';
      // エラー表示, 共通エラー
      const errorTitle = 'message.common_title_error';
      const titleMap = {
        start: executionTitle,
        success: successTitle,
        reExecute: confirmationTitle,
        timeOut: errorTitle,
        failed: errorTitle,
      };
      return this.$t(titleMap[this.phase]);
    },

    workSupportMessage() {
      const messageMap = {
        start: this.displayInfo,
        success: `${this.i18nWsKey}.REQ_WS_54_TXT_1_1002`,
        reExecute: `${this.i18nWsKey}.REQ_WS_54_TXT_2_1002`,
        timeOut: `${this.i18nWsKey}.REQ_WS_54_TXT_3`,
        failed: 'individual.error_communication_error',
      };
      return this.$t(messageMap[this.phase]);
    },

    showButtonOk() {
      const showOkButtonMap = {
        success: true,
        timeOut: true,
        failed: true,
      };
      let result = showOkButtonMap[this.phase];
      if (!result) {
        // 合致しない場合は false とする。
        result = false;
      }
      return result;
    }
  },

  methods: {
    ...mapActions('auth', ['logout', 'resetSession']),

    /** 初期化処理 */
    init() {
      this.modal_requirement_054 = true;
      // EUCキーの取得
      this.systemId = individualDiagnose.getCurrentSystemId(this);
    },

    /** 実行処理 */
    async execute() {
      try {
        this.isLoading = true;
        // バックエンドAPI POST,GET
        const token = session.getToken();
        const response = await individualWorkSupportUtil.pollingWspApi(
          this,
          this.netAppId,
          this.systemId,
          this.workSupportId,
          {
            id_token: token.id_token,
            type: 'execute',
            is_first: this.isFirst
          },
          // エラー発生のコールバックを実装
          // ここでは利用していないが、引数の errorType にはあらかじめ用意された定数文字列が返ってくる。
          (errorType) => { errorType; this.showUnexpectedError(); },
          // POST API 結果が異常の場合（handler.validate でエラーが発生した場合）のコールバックを定義する（validateErrorResult の中は同様のため、省略する）
          (result) => { this.validateErrorResult(result); },
          // GET API 結果が異常の場合（handler.validate でエラーが発生した場合）のコールバックを定義する（validateErrorResult の中は同様のため、省略する）
          (result) => { this.validateErrorResult(result); },
        );

        if (!response) return;

        switch (response.data.status) {
          case REQUEST_STATUS_COMPLETED:
            // 正常終了
            switch (response.data.user_data.result) {
              case 'ok':
                if (this.isFirst) {
                  this.isFirst = false;
                  // 正常終了表示画面へ遷移
                  this.phase = PHASE.SUCCESS;
                } else {
                  // 再実行確認表示画面へ遷移
                  this.phase = PHASE.RE_EXECUTE;
                }
                break;
              case 'ng':
                // タイムアウト処理の開始
                this.phase = PHASE.TIME_OUT;
                break;
              default:
                // 汎用エラー表示
                this.showUnexpectedError();
                break;
            }
            break;
          case REQUEST_STATUS_FAILED:
          default:
            // 異常終了：汎用エラー
            this.showUnexpectedError();
            return;
        }
      } finally {
        this.isLoading = false;
      }
    },

    /** 再実行確認表示 */
    async reExecuteProcess() {
      // 再実行確認表示画面へ遷移
      this.phase = PHASE.RE_EXECUTE;
    },
    /** 切り替えボタン押下 */
    async executeProcess() {
      this.displayInfo = this.$t(`individual.label_wait`);
      // 初期画面へ遷移
      this.phase = PHASE.START;
      this.execute();
    },

    /** 通常終了処理 */
    async executeEndProcess() {
      // exit処理を呼び出す
      await this.exit(false);
    },

    /** 共通エラー終了処理 */
    async unexpectedErrorProcess() {
      // exit処理を呼び出す
      await this.exit(true);
      this.closeWizard();
    },

    /**
     * exit処理
     * @param {boolean} isUnexpectedError 共通エラーフラグ
     */
    async exit(isUnexpectedError = false) {
      try {
        this.isLoading = true;
        // バックエンドAPI POST,GET
        const token = session.getToken();
        const response = await individualWorkSupportUtil.pollingWspApi(
          this,
          this.netAppId,
          this.systemId,
          this.workSupportId,
          {
            id_token: token.id_token,
            type: 'exit'
          },
          // エラー発生のコールバックを実装
          // ここでは利用していないが、引数の errorType にはあらかじめ用意された定数文字列が返ってくる。
          (errorType) => { errorType; this.showUnexpectedError(); },
          // POST API 結果が異常の場合（handler.validate でエラーが発生した場合）のコールバックを定義する（validateErrorResult の中は同様のため、省略する）
          (result) => { this.validateErrorResult(result); },
          // GET API 結果が異常の場合（handler.validate でエラーが発生した場合）のコールバックを定義する（validateErrorResult の中は同様のため、省略する）
          (result) => { this.validateErrorResult(result); },
        );

        if (!response) return;

        if (isUnexpectedError) return;

        switch (response.data.status) {
          case REQUEST_STATUS_COMPLETED:
            // 正常終了
            this.closeWizard();
            break;
          case REQUEST_STATUS_FAILED:
          default:
            // 異常終了：汎用エラー
            this.showUnexpectedError();
            return;
        }
      } finally {
        this.isLoading = false;
      }
    },

    /**
      * API 通信で共通バリデーションでエラーが発生した際のコールバック
      * @param {object} result バリデーション結果
      */
    validateErrorResult(result) {
      apiErrorDefaultAction(
        this,
        result,
        // ログアウト処理のコールバック
        (self) => { self; this.logout(this); },
        // セッションリセット処理のコールバック
        (self) => { self; this.resetSession(this); },
        // その他エラー発生のコールバック
        (result) => { result; this.showUnexpectedError(); },
      );
    },

    /** その他エラーの表示 */
    showUnexpectedError() {
      this.phase = PHASE.FAILED;
    },

    /** Wizardを閉じる */
    async closeWizard() {
      this.$emit('close-wizard');
      this.modal_requirement_054 = false;
    },

    async onOkClicked() {
      switch (this.phase) {
        case PHASE.SUCCESS:
          await this.reExecuteProcess();
          break;
        case PHASE.TIME_OUT:
          await this.executeEndProcess();
          break;
        case PHASE.FAILED:
          await this.unexpectedErrorProcess();
          break;
        default:
          break;
      }
    },
  },

  async mounted() {
    this.init();
    // 実行処理
    this.execute();
  },
};